import { Drawer, Stack, Divider } from '@mantine/core';
import { Practitioner } from '@medplum/fhirtypes';
import { useMedplumProfile } from '@medplum/react';
import { useState, useEffect } from 'react';
import { AvailabilityType, AvailabilityTypeEnum } from '@bariendo/lib/scheduling';
import { AvailabilityTypeInput } from '@/pages/SchedulingPage/components/AvailabilityTypeInput';
import { WeeklyAvailabilityForm } from './WeeklyAvailabilityForm';
import { OneTimeAvailabilityForm } from './OneTimeAvailabilityForm';
import { PractitionerWithRole } from '../types';
import { getReferenceString } from '@medplum/core';

interface AvailabilityDrawerProps {
  opened: boolean;
  onClose: () => void;
  initialPractitionerOverride?: Practitioner | null;
  initialAvailabilityType: AvailabilityType;
  slotIdToEdit?: string;
  onSaveSuccess: (providerId: string | undefined, type: AvailabilityType) => void;
  practitioners?: PractitionerWithRole[];
}

export function AvailabilityDrawer({
  opened,
  onClose,
  initialPractitionerOverride = null,
  initialAvailabilityType,
  slotIdToEdit,
  onSaveSuccess,
  practitioners,
}: AvailabilityDrawerProps) {
  const currentUser = useMedplumProfile() as Practitioner | undefined;
  const [availabilityType, setAvailabilityType] = useState<AvailabilityType>(AvailabilityTypeEnum.WEEKLY);
  const [initialPractitioner, setInitialPractitioner] = useState<PractitionerWithRole | null>(null);

  useEffect(() => {
    if (!opened) {
      setInitialPractitioner(null);
      return;
    }
    if (initialPractitionerOverride) {
      setInitialPractitioner(initialPractitionerOverride);
    } else if (currentUser) {
      const currentUserProvider = practitioners?.find((p) => getReferenceString(p) === getReferenceString(currentUser));
      if (!currentUserProvider) {
        setInitialPractitioner(null);
        return;
      }
      setInitialPractitioner(currentUserProvider);
    } else {
      setInitialPractitioner(null);
    }
  }, [opened, currentUser, initialPractitionerOverride]);

  useEffect(() => {
    if (opened && initialAvailabilityType) {
      setAvailabilityType(initialAvailabilityType);
    } else if (!opened) {
      setAvailabilityType(AvailabilityTypeEnum.WEEKLY);
    }
  }, [opened, initialAvailabilityType]);

  const handleClose = () => {
    setAvailabilityType(AvailabilityTypeEnum.WEEKLY);
    setInitialPractitioner(null);
    onClose();
  };

  return (
    <Drawer.Root opened={opened} onClose={handleClose} position="right" size="sm">
      <Drawer.Overlay />

      <Drawer.Content>
        <Drawer.Header>
          <Drawer.Title fw="bold">Availability</Drawer.Title>
          <Drawer.CloseButton />
        </Drawer.Header>

        <Divider />

        <Drawer.Body>
          <Stack gap="sm" py="sm">
            <AvailabilityTypeInput value={availabilityType} onChange={setAvailabilityType} />

            {availabilityType === AvailabilityTypeEnum.WEEKLY && (
              <WeeklyAvailabilityForm
                initialPractitioner={initialPractitioner}
                onClose={handleClose}
                onSaveSuccess={onSaveSuccess}
                practitioners={practitioners}
              />
            )}

            {availabilityType === AvailabilityTypeEnum.ONETIME && (
              <OneTimeAvailabilityForm
                initialPractitioner={initialPractitioner}
                onClose={handleClose}
                slotIdToEdit={slotIdToEdit ?? null}
                onSaveSuccess={onSaveSuccess}
                practitioners={practitioners}
              />
            )}
          </Stack>
        </Drawer.Body>
      </Drawer.Content>
    </Drawer.Root>
  );
}
